// Paged + light loading of internal notes (GET /stats/) for the position modal.
//
// Every name of the server contract lives here so a rename on the back end is a
// one-line change. `page`/`per` are the params Stat.special_actions already
// passes to the kaminari `pagination` scope (rebase_server
// app/models/concerns/pagination.rb); `light` opts into the slim payload.
//
// Total count: the stats index answers with a bare array today. With `light=1`
// the server may answer `{ data: [...], meta: { total_records } }` (the shape
// CompanyEmployeesController#index already uses). Both shapes are accepted;
// without a total the counter is hidden and paging stops on a short page.

export const NOTES_PAGE_PARAM = 'page'
export const NOTES_PER_PARAM = 'per'
export const NOTES_LIGHT_PARAM = 'light'
export const NOTES_PER_PAGE = 30
export const NOTES_SORT_NEWEST_FIRST = '-created_at'

// Fields of a note the paged (light) view reads. Kept in one place so the
// back-end light serializer and this list can be compared directly.
// applicant_id / applicant_name: only for notes on an Offer (the offer's
// applicant: applicants.id and "first last"); null/absent elsewhere.
export const LIGHT_NOTE_FIELDS = ['id', 'owner', 'content', 'created_at', 'resource_type', 'resource_id', 'applicant_id', 'applicant_name']

export const buildPagedParams = (baseParams, page, per = NOTES_PER_PAGE) => ({
  ...baseParams,
  [NOTES_PAGE_PARAM]: page,
  [NOTES_PER_PARAM]: per,
  [NOTES_LIGHT_PARAM]: 1
})

const toCount = value => {
  const number = Number(value)
  return value !== null && value !== undefined && value !== '' && Number.isFinite(number) && number >= 0 ? number : null
}

// -> { items: Array, total: Number|null }
export const parsePagedResponse = response => {
  if (Array.isArray(response)) {
    return { items: response, total: null }
  }
  if (response && Array.isArray(response.data)) {
    const meta = response.meta || {}
    const total = toCount(meta.total_records !== undefined ? meta.total_records : meta.total_count)
    return { items: response.data, total }
  }
  return { items: [], total: null }
}

// Appends `incoming` after `existing`, skipping ids already shown (a note added
// while paging shifts the server's offsets, so the next page can repeat one).
export const mergeNotes = (existing, incoming) => {
  const seen = new Set(existing.map(note => note.id))
  const merged = existing.slice()
  incoming.forEach(note => {
    if (note && !seen.has(note.id)) {
      seen.add(note.id)
      merged.push(note)
    }
  })
  return merged
}

// Puts a freshly created note at the top, replacing an existing copy if any.
export const prependNote = (existing, note) => [note].concat(existing.filter(item => item.id !== note.id))

// Whether to offer "טען עוד". The server total is display-only: it can grow
// while someone pages (a colleague adds a note), so `loaded < total` could stay
// true forever. Stop on a short page, or on a page that added nothing new.
export const hasMoreNotes = ({ lastPageSize, newCount, per = NOTES_PER_PAGE }) =>
  lastPageSize >= per && newCount > 0

// Counter text values; never "loaded > total".
export const notesCounter = (loaded, total) => {
  if (total === null || total === undefined) return null
  return { loaded, total: Math.max(total, loaded) }
}

// Would the list's current server-side filter return this note? Mirrors
// Stat.filter_by_min_priority (priority >= min) and filter_by_designator
// (comma list). Used for a note created locally, before any reload.
export const noteMatchesFilter = (note, { min_priority, designator } = {}) => {
  if (!note) return false
  if (min_priority !== undefined && min_priority !== null && note.priority !== undefined && note.priority !== null &&
      Number(note.priority) < Number(min_priority)) {
    return false
  }
  if (designator) {
    return String(designator).split(',').includes(note.designator)
  }
  return true
}
